import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  LocationOn as LocationIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useAdmin } from "../context/AdminContext";
import {
  formatDate,
  isDriverAvailableIn24Hours,
  formatPhoneNumber,
  formatStatusLabel,
} from "../utils/helpers";
// import d1Image from "../assets/images/D1.webp";
// import d2Image from "../assets/images/D2.webp";

const DriverDetail = () => {
  const { drivers, tasks, vehicles } = useAdmin();
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const selectedDriver = drivers.find((d) => d.id === selectedDriverId);
  const driverTasks = tasks.filter((t) => t.driverId === selectedDriverId);

  const resolveDriverIdentifiers = (driver = null) =>
    Array.from(
      new Set([driver?.userId, driver?.uid, driver?.id].filter(Boolean))
    ).map((identifier) => String(identifier).trim());

  const resolveAssignedFleetVehicle = (driver = null) => {
    if (!driver) {
      return null;
    }

    const assignedVehicleId =
      driver?.assignedVehicle?.id || driver?.assignedVehicleId;

    if (assignedVehicleId) {
      const matchedVehicle = vehicles.find(
        (vehicle) => vehicle.id === assignedVehicleId
      );
      if (matchedVehicle) {
        return matchedVehicle;
      }
    }

    const identifiers = new Set(resolveDriverIdentifiers(driver));
    const matchedByAssignedDriver = vehicles.find((vehicle) => {
      const assignedDriver = String(vehicle?.assignedDriver || "").trim();
      return assignedDriver && identifiers.has(assignedDriver);
    });

    return matchedByAssignedDriver || driver?.assignedVehicle || null;
  };

  const selectedDriverAssignedVehicle = resolveAssignedFleetVehicle(selectedDriver);

  // Debug: Log selected driver to see what fields exist
  React.useEffect(() => {
    if (selectedDriver) {
      console.log('Selected Driver Data:', selectedDriver);
      console.log('Document URLs:', {
        licenseFrontUrl: selectedDriver.licenseFrontUrl,
        licenseBackUrl: selectedDriver.licenseBackUrl,
        industryAuthorityFrontUrl: selectedDriver.industryAuthorityFrontUrl,
        industryAuthorityBackUrl: selectedDriver.industryAuthorityBackUrl,
        identityFrontUrl: selectedDriver.identityFrontUrl,
        identityBackUrl: selectedDriver.identityBackUrl,
        vehicleDocUrl: selectedDriver.vehicleDocUrl,
        vehicleDocBackUrl: selectedDriver.vehicleDocBackUrl,
        insuranceDocUrl: selectedDriver.insuranceDocUrl,
        insuranceDocBackUrl: selectedDriver.insuranceDocBackUrl,
        coiUrl: selectedDriver.coiUrl,
      });
    }
  }, [selectedDriver]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ mb: 1, fontWeight: "bold", color: "#000" }}
        >
          Driver Details & Availability
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View comprehensive driver information, vehicle details, and task
          history
        </Typography>
      </Box>

      {/* Driver Selection */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          borderRadius: 2,
        }}
      >
        <TextField
          select
          label="Select Driver"
          value={selectedDriverId}
          onChange={(e) => setSelectedDriverId(e.target.value)}
          fullWidth
          placeholder="Choose a driver to view details"
          InputProps={{
            startAdornment: (
              <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
        >
          {drivers.length === 0 ? (
            <MenuItem disabled>No drivers available</MenuItem>
          ) : (
            drivers.map((driver, driverIndex) => {
              // const avatarImage = driverIndex % 2 === 0 ? d1Image : d2Image;
              return (
                <MenuItem key={driver.id} value={driver.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      src={driver.profilePictureUrl || ""}
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        bgcolor: "#f5f5f5",
                        fontSize: "0.875rem",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {driver.name?.substring(0, 2).toUpperCase() || "DR"}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {driver.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {driver.email}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })
          )}
        </TextField>
      </Paper>

      {selectedDriver ? (
        <Grid container spacing={3}>
          {/* Driver Info Card */}
          <Grid item size={{ xs: 12, lg: 4 }}>
            <Paper
              sx={{
                p: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 2,
                background:
                  "linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography
                  variant="caption"
                  component="a"
                  href={selectedDriver.profilePictureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "#DD2424",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Avatar
                    src={selectedDriver.profilePictureUrl || ""}
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: "#f5f5f5",
                      fontSize: 48,
                      mb: 2,
                      border: "4px solid #e0e0e0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    {selectedDriver.name?.substring(0, 2).toUpperCase() || "DR"}
                  </Avatar>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
                >
                  {selectedDriver.name || "N/A"}
                </Typography>
                <Chip
                  label={
                    selectedDriver.approved
                      ? "Approved Driver"
                      : selectedDriver.status || "Pending Approval"
                  }
                  color={selectedDriver.approved ? "success" : "warning"}
                  icon={selectedDriver.approved ? <CheckIcon /> : undefined}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Personal Information */}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 2, color: "#DD2424" }}
              >
                Personal Information
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}
              >
                {selectedDriver.dateOfBirth && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Date of Birth
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.dateOfBirth}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.gender && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <PersonIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Gender
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.gender}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.nationality && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <LocationIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Nationality
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.nationality}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Contact Information */}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 2, color: "#DD2424" }}
              >
                Contact Information
              </Typography>
              <List dense sx={{ mb: 2 }}>
                <ListItem sx={{ px: 0 }}>
                  <EmailIcon sx={{ mr: 2, color: "#DD2424", fontSize: 20 }} />
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedDriver.email || "N/A"}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <PhoneIcon sx={{ mr: 2, color: "#DD2424", fontSize: 20 }} />
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatPhoneNumber(selectedDriver.phone)}
                      </Typography>
                    }
                  />
                </ListItem>
                {selectedDriver.address && (
                  <ListItem sx={{ px: 0 }}>
                    <LocationIcon
                      sx={{ mr: 2, color: "#DD2424", fontSize: 20 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          Address
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedDriver.address}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Driver Credentials */}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 2, color: "#DD2424" }}
              >
                Driver Credentials
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {selectedDriver.driverId && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <BadgeIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Driver ID
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.driverId}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.licenseNumber && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <DocumentIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        License Number
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.licenseNumber}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.licenseExpiry && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        License Expiry
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {formatDate(selectedDriver.licenseExpiry)}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.licenseClass && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <BadgeIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        License Class
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.licenseClass}
                    </Typography>
                  </Box>
                )}
                {selectedDriver.yearsOfExperience && (
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <ScheduleIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Years of Experience
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                      {selectedDriver.yearsOfExperience} years
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <CalendarIcon
                      sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Member Since
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, ml: 3 }}>
                    {formatDate(selectedDriver.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Own Vehicle Card */}
            {selectedDriver.ownVehicle && (
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  bgcolor: "#f8f9fa",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CarIcon sx={{ mr: 1, color: "#DD2424", fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Own Vehicle
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Type:
                    </Typography>
                    <Chip
                      label={selectedDriver.ownVehicle.type || "N/A"}
                      size="small"
                      sx={{
                        bgcolor: "#DD242420",
                        color: "#DD2424",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  {selectedDriver.ownVehicle.make && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Make:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedDriver.ownVehicle.make}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Model:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriver.ownVehicle.model || "N/A"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Plate Number:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriver.ownVehicle.plateNumber || "N/A"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Capacity:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriver.ownVehicle.seats || "N/A"} seats
                    </Typography>
                  </Box>
                  {selectedDriver.ownVehicle.year && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Year:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedDriver.ownVehicle.year}
                      </Typography>
                    </Box>
                  )}
                  {selectedDriver.ownVehicle.color && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Color:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedDriver.ownVehicle.color}
                      </Typography>
                    </Box>
                  )}
                  {selectedDriver.ownVehicle.vin && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        VIN:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      >
                        {selectedDriver.ownVehicle.vin}
                      </Typography>
                    </Box>
                  )}
                  {selectedDriver.ownVehicle.insuranceExpiry && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Insurance Expiry:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedDriver.ownVehicle.insuranceExpiry)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {selectedDriverAssignedVehicle && (
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  bgcolor: "#f8f9fa",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CarIcon sx={{ mr: 1, color: "#DD2424", fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Assigned Fleet Vehicle
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Type:
                    </Typography>
                    <Chip
                      label={
                        selectedDriverAssignedVehicle.vehicleType ||
                        selectedDriverAssignedVehicle.type ||
                        "N/A"
                      }
                      size="small"
                      sx={{
                        bgcolor: "#DD242420",
                        color: "#DD2424",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Model:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriverAssignedVehicle.model || "N/A"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Plate Number:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriverAssignedVehicle.licensePlate ||
                        selectedDriverAssignedVehicle.plateNumber ||
                        "N/A"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Capacity:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDriverAssignedVehicle.capacity ||
                        selectedDriverAssignedVehicle.seats ||
                        "N/A"}{" "}
                      seats
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatStatusLabel(
                        selectedDriverAssignedVehicle.status,
                        "Assigned"
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Vehicle Inspection */}
            {/* {selectedDriver.vehicleInspection && selectedDriver.ownVehicle && (
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  bgcolor: "#f8f9fa",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CheckIcon sx={{ mr: 1, color: "#DD2424", fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Vehicle Inspection Checklist
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q1 - Vehicle Exterior Condition
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Body work, paint, and overall exterior condition
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q1 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q1
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q2 - Interior Cleanliness
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Seats, dashboard, and interior cleanliness
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q2 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q2
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q3 - Lights & Signals
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        All lights, indicators, and signals functioning
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q3 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q3
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q4 - Tires & Brakes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tire condition, tread depth, and brake functionality
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q4 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q4
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q5 - Safety Equipment
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Seatbelts, airbags, and emergency equipment
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q5 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q5
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Q6 - Mechanical Condition
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Engine, fluids, and overall mechanical soundness
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDriver.vehicleInspection.q6 ? "Pass" : "Fail"}
                      size="small"
                      color={
                        selectedDriver.vehicleInspection.q6
                          ? "success"
                          : "error"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </Paper>
            )} */}
          </Grid>

          {/* Availability & Tasks */}
          <Grid item size={{ xs: 12, lg: 8 }}>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <Card
                  sx={{
                    bgcolor: "#f8f9fa",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <ScheduleIcon
                      sx={{ fontSize: 40, color: "#DD2424", mb: 1 }}
                    />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", color: "#000" }}
                    >
                      {driverTasks.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Tasks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <Card
                  sx={{
                    bgcolor: "#e8f5e9",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <CheckIcon sx={{ fontSize: 40, color: "#4caf50", mb: 1 }} />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", color: "#4caf50" }}
                    >
                      {
                        driverTasks.filter((t) => t.status === "completed")
                          .length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <Card
                  sx={{
                    bgcolor: "#fff3e0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <AssignmentIcon
                      sx={{ fontSize: 40, color: "#ff9800", mb: 1 }}
                    />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", color: "#ff9800" }}
                    >
                      {
                        driverTasks.filter(
                          (t) =>
                            t.status === "assigned" ||
                            t.status === "in-progress"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Tasks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <Card
                  sx={{
                    bgcolor: isDriverAvailableIn24Hours(selectedDriver)
                      ? "#e8f5e9"
                      : "#fafafa",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    border: 1,
                    borderColor: isDriverAvailableIn24Hours(selectedDriver)
                      ? "#4caf50"
                      : "#e0e0e0",
                  }}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <CalendarIcon
                      sx={{
                        fontSize: 40,
                        color: isDriverAvailableIn24Hours(selectedDriver)
                          ? "#4caf50"
                          : "#757575",
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      {(() => {
                        // Check new availabilitySchedule system
                        if (
                          selectedDriver.availabilitySchedule &&
                          Array.isArray(selectedDriver.availabilitySchedule) &&
                          selectedDriver.availabilitySchedule.length > 0
                        ) {
                          const now = new Date();
                          const activeSchedules =
                            selectedDriver.availabilitySchedule.filter(
                              (avail) => {
                                if (!avail.isAvailable) return false;
                                const endDate = avail.endDate?.toDate
                                  ? avail.endDate.toDate()
                                  : new Date(avail.endDate);
                                return endDate > now;
                              }
                            );
                          if (activeSchedules.length > 0) {
                            // Show the earliest end date
                            const earliestEnd = activeSchedules.reduce(
                              (earliest, current) => {
                                const currentEnd = current.endDate?.toDate
                                  ? current.endDate.toDate()
                                  : new Date(current.endDate);
                                const earliestEnd = earliest.endDate?.toDate
                                  ? earliest.endDate.toDate()
                                  : new Date(earliest.endDate);
                                return currentEnd < earliestEnd
                                  ? current
                                  : earliest;
                              }
                            );
                            const endDate = earliestEnd.endDate?.toDate
                              ? earliestEnd.endDate.toDate()
                              : new Date(earliestEnd.endDate);
                            return formatDate(endDate);
                          }
                          return "See Schedule";
                        }
                        // Fallback to old availableUntil field
                        return selectedDriver.availableUntil
                          ? formatDate(selectedDriver.availableUntil)
                          : "Anytime";
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Available Until
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Uploaded Documents */}
            <Paper
              sx={{
                p: 3,
                mt: 3,
                mb: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DocumentIcon sx={{ mr: 1, color: "#DD2424", fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Uploaded Documents
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Driver's License */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Driver's License
                      </Typography>
                      {selectedDriver.licenseFrontUrl ||
                      selectedDriver.licenseBackUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.licenseExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.licenseExpiry}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedDriver.licenseFrontUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.licenseFrontUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Front
                        </Typography>
                      )}
                      {selectedDriver.licenseBackUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.licenseBackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Back
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Insurance Document */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Insurance Document
                      </Typography>
                      {selectedDriver.insuranceDocUrl ||
                      selectedDriver.insuranceDocBackUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.insuranceExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.insuranceExpiry}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedDriver.insuranceDocUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.insuranceDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Front
                        </Typography>
                      )}
                      {selectedDriver.insuranceDocBackUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.insuranceDocBackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Back
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Vehicle Registration */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Vehicle Registration
                      </Typography>
                      {selectedDriver.vehicleDocUrl ||
                      selectedDriver.vehicleDocBackUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.vehicleRegistrationExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.vehicleRegistrationExpiry}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedDriver.vehicleDocUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.vehicleDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Front
                        </Typography>
                      )}
                      {selectedDriver.vehicleDocBackUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.vehicleDocBackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Back
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Identity Document (Passport/ID) */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Identity Document ({selectedDriver.identityType || "ID"}
                        )
                      </Typography>
                      {selectedDriver.identityFrontUrl ||
                      selectedDriver.identityBackUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.identityExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.identityExpiry}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedDriver.identityFrontUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.identityFrontUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Front
                        </Typography>
                      )}
                      {selectedDriver.identityBackUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.identityBackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Back
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Industry Authority Document */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Industry Authority Certificate
                      </Typography>
                      {selectedDriver.industryAuthorityFrontUrl ||
                      selectedDriver.industryAuthorityBackUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.industryAuthorityExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.industryAuthorityExpiry}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedDriver.industryAuthorityFrontUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.industryAuthorityFrontUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Front
                        </Typography>
                      )}
                      {selectedDriver.industryAuthorityBackUrl && (
                        <Typography
                          variant="caption"
                          component="a"
                          href={selectedDriver.industryAuthorityBackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: "#DD2424",
                            px: 1,
                            py: 0.5,
                            color: "#DD2424",
                            textDecoration: "none",
                            "&:hover": {
                              color: "#fff",
                              backgroundColor: "#000",
                              borderColor: "#000",
                              transition: "0.3s",
                            },
                          }}
                        >
                          View Back
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Certificate of Insurance (COI) */}
                <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                  <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Certificate of Insurance (COI)
                      </Typography>
                      {selectedDriver.coiUrl ? (
                        <Chip
                          label="Uploaded"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Not Uploaded"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                    {selectedDriver.coiExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Expires: {selectedDriver.coiExpiry}
                      </Typography>
                    )}
                    {selectedDriver.coiUrl && (
                      <Typography
                        variant="caption"
                        component="a"
                        href={selectedDriver.coiUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          borderRadius: 1,
                          border: 1,
                          borderColor: "#DD2424",
                          px: 1,
                          py: 0.8,
                          color: "#DD2424",
                          textDecoration: "none",
                          "&:hover": {
                            color: "#fff",
                            backgroundColor: "#000",
                            borderColor: "#000",
                            transition: "0.3s",
                          },
                        }}
                      >
                        View Document
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Profile Photo */}
                {/* <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                <Box sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Profile Photo
                    </Typography>
                    {selectedDriver.profilePictureUrl ? (
                      <Chip
                        label="Uploaded"
                        size="small"
                        color="success"
                        icon={<CheckIcon />}
                      />
                    ) : (
                      <Chip label="Not Uploaded" size="small" color="default" />
                    )}
                  </Box>
                  {selectedDriver.profilePictureUrl && (
                    <Typography
                      variant="caption"
                      component="a"
                      href={selectedDriver.profilePictureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "#DD2424",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      View Photo →
                    </Typography>
                  )}
                </Box>
                </Grid> */}
              </Box>
            </Paper>

            {/* Task History */}
            <Paper
              sx={{
                p: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <AssignmentIcon
                  sx={{ mr: 1, color: "#DD2424", fontSize: 28 }}
                />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Task History
                </Typography>
              </Box>
              {driverTasks.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <AssignmentIcon
                    sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No tasks assigned yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Task assignments will appear here
                  </Typography>
                </Box>
              ) : (
                <List sx={{ bgcolor: "#fafafa", borderRadius: 1 }}>
                  {driverTasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                      <ListItem
                        sx={{
                          px: 2.5,
                          py: 2.5,
                          borderRadius: 1,
                          bgcolor: "#fff",
                          mb: index < driverTasks.length - 1 ? 1 : 0,
                          transition: "all 0.2s",
                          "&:hover": {
                            bgcolor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              mb: 1.5,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "bold", mb: 0.5 }}
                              >
                                {task.pickupLocation ||
                                  task.location ||
                                  "Unknown Location"}
                              </Typography>
                              {task.dropoffLocation &&
                                task.dropoffLocation !==
                                  task.pickupLocation && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <LocationIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      → {task.dropoffLocation}
                                    </Typography>
                                  </Box>
                                )}
                            </Box>
                            <Chip
                              label={formatStatusLabel(task.status, "Assigned")}
                              color={
                                task.status === "completed"
                                  ? "success"
                                  : task.status === "cancelled"
                                  ? "error"
                                  : task.status === "in-progress"
                                  ? "warning"
                                  : "info"
                              }
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <CalendarIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(task.startTime)}
                              {task.endTime && ` → ${formatDate(task.endTime)}`}
                            </Typography>
                          </Box>

                          {task.passengerCount && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <PersonIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {task.passengerCount} passenger
                                {task.passengerCount > 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          )}

                          {task.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1.5,
                                p: 1.5,
                                bgcolor: "#f5f5f5",
                                borderRadius: 1,
                              }}
                            >
                              {task.description}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            {task.priority && (
                              <Chip
                                label={`Priority: ${formatStatusLabel(task.priority, "Medium")}`}
                                size="small"
                                variant="outlined"
                                color={
                                  task.priority === "high"
                                    ? "error"
                                    : task.priority === "low"
                                    ? "default"
                                    : "warning"
                                }
                              />
                            )}
                            {task.vehicleSource && (
                              <Chip
                                label={
                                  task.vehicleSource === "own"
                                    ? "Own Vehicle"
                                    : "Garage Vehicle"
                                }
                                size="small"
                                variant="outlined"
                                icon={<CarIcon />}
                              />
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Paper
          sx={{
            p: 8,
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            borderRadius: 2,
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Please select a driver to view details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose a driver from the dropdown above to see their complete
            profile
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DriverDetail;
